"use client";

import { cn } from "@/lib/utils";
import Link from "next/link";
import React, { useState } from "react";
import { motion, AnimatePresence, type Variants } from "framer-motion";

type MenuGroup = {
  title: string;
  img: string;
  items: { name: string }[];
};

const menus: MenuGroup[] = [
  {
    title: "Kebab & Grills",
    img: "/imgs/menu-2.png",
    items: [
      { name: "Shish Tawook" },
      { name: "Kofta Kebab" },
      { name: "Mixed Grill" },
    ],
  },
  {
    title: "Tagine & Eqdah",
    img: "/imgs/menu-1.png",
    items: [
      { name: "Chicken Tagine" },
      { name: "Lamb Tagine" },
      { name: "Vegetable Tagine" },
      { name: "Fish Tagine" },
    ],
  },
  {
    title: "Chicken & Meat",
    img: "/imgs/menu-3.png",
    items: [
      { name: "Grilled Chicken" },
      { name: "Lamb Kebabs" },
      { name: "Beef Shawarma" },
      { name: "Mixed Meat Platter" },
    ],
  },
  {
    title: "Rice & Sides",
    img: "/imgs/menu-4.png",
    items: [
      { name: "Arabian Rice" },
      { name: "Fattoush Salad" },
      { name: "Hummus" },
      { name: "Baba Ganoush" },
    ],
  },
  {
    title: "Desserts",
    img: "/imgs/menu-5.png",
    items: [
      { name: "Baklava" },
      { name: "Kunafa" },
      { name: "Umm Ali" },
      { name: "Mahalabia" },
    ],
  },
  {
    title: "Coffee & Drinks",
    img: "/imgs/menu-6.png",
    items: [
      { name: "Arabic Coffee" },
      { name: "Mint Tea" },
      { name: "Jallab" },
      { name: "Ayran" },
    ],
  },
];

export default function MenuSection() {
  const [openKey, setOpenKey] = useState<string>("");

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.3 },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
    hover: {
      scale: 1.02,
      transition: { duration: 0.3, ease: "easeOut" },
    },
  };

  const menuItemVariants: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="py-10 xl:py-[120px]"
    >
      <div className="max-w-[1569px] w-full mx-auto px-5">
        {/* Header */}
        <motion.div
          className="center text-center flex-col sm:gap-8.75"
          variants={containerVariants}
        >
          <motion.h1
            variants={itemVariants}
            className="text-[37.78px] sm:text-[75px] leading-[86%] font-bold font-amiri text-spring-wood"
          >
            Discover the Flavors of Almajlis
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="max-w-[906px] w-full mx-auto text-[11.11px] sm:text-[25px] leading-[127%] text-old-lace sm:my-0 my-3"
          >
            From cozy dinners to takeaway favorites and catering for your
            special occasions — experience our food your way.
          </motion.p>

          <motion.div variants={itemVariants}>
            <Link
              href="/menu"
              className="shrink-0 max-w-[140.55px] sm:max-w-[180px] w-full btn_gradient relative isolate flex justify-center items-center overflow-hidden group"
              aria-label="View full menu"
            >
              <motion.span
                className="text-[7.21px] sm:text-xs uppercase relative z-10"
                whileHover={{ scale: 1.05 }}
              >
                View full menu
              </motion.span>
              <div className="size-full absolute inset-0 bg-gradient-to-b from-[#FFFFFF]/[7%] to-[#FFFFFF]/15 z-[-2]" />
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-[#FFFFFF]/10 to-[#FFFFFF]/5 z-[-1]"
                initial={{ x: "-100%" }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </Link>
          </motion.div>
        </motion.div>

        {/* Grid */}
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-3 gap-x-[7px] gap-y-5 sm:gap-5 mt-[17px] sm:mt-[30px]"
          variants={containerVariants}
        >
          {menus.map((menu) => {
            const isOpen = openKey === menu.title;
            return (
              <motion.div
                key={menu.title}
                className="h-[69.87px] sm:h-[284px] rounded-[15px] center bg-center bg-cover bg-no-repeat border border-transparent hover:border-white/50 transition-all duration-300 overflow-hidden relative cursor-pointer"
                style={{ backgroundImage: `url(${menu.img})` }}
                variants={itemVariants}
                whileHover="hover"
                onMouseEnter={() => setOpenKey(menu.title)}
                onMouseLeave={() => setOpenKey("")}
                onClick={() =>
                  setOpenKey((k) => (k === menu.title ? "" : menu.title))
                }
                role="button"
                aria-expanded={isOpen}
                aria-label={`${menu.title} quick items`}
              >
                {/* darken for readability on hover/open */}
                <motion.div
                  className="absolute inset-0 bg-black/30"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: isOpen ? 1 : 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                />

                <div className="space-y-[10px] sm:space-y-[66px] relative z-10 px-2">
                  <motion.h2
                    className="text-[12.48px] sm:text-[35px] leading-[86%] font-bold font-amiri text-center text-white"
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    {menu.title}
                  </motion.h2>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        className={cn(
                          "grid grid-cols-2 max-w-full sm:w-[456px] bg-black/70 backdrop-blur-sm p-3 sm:p-4 rounded-lg mx-auto"
                        )}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                      >
                        {menu.items.map((item, i) => (
                          <motion.div
                            key={`${menu.title}-${item.name}`}
                            className={cn(
                              "py-[8px] sm:py-[10px] center border-dashed border-white/40",
                              i <= 1 ? "border-b-2" : "border-b-0",
                              i % 2 === 0 ? "border-r-2" : "border-r-0"
                            )}
                            variants={menuItemVariants}
                            initial="hidden"
                            animate="visible"
                            transition={{ delay: i * 0.05 }}
                          >
                            <p className="text-[12px] sm:text-xl font-medium text-white text-center">
                              {item.name}
                            </p>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </motion.section>
  );
}
